import { AdCreative, AdCreativeType, FBCallToAction } from '@marketing-milk/interfaces'
import { LocalAdPreviewCreative } from 'components/ad-preview'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'

/**
 * Which asset and which copy variant the review surface is currently showing for
 * a dynamic ad.
 *
 * A dynamic ad has no single rendering — Facebook mixes one variant from each
 * pool with one asset at delivery. Pinning one combination is what lets a
 * reviewer pick a variant that worries them, hold the rest fixed, and see the
 * result as an actual ad rather than an inventory of parts. Purely a view
 * control: never persisted, never sent to Facebook.
 */
export interface DynamicCombination {
  asset: number
  primaryText: number
  headline: number
  description: number
  callToAction: number
}

export const FIRST_COMBINATION: DynamicCombination = {
  asset: 0,
  primaryText: 0,
  headline: 0,
  description: 0,
  callToAction: 0,
}

export type DynamicAsset =
  | { kind: 'image'; url?: string }
  | { kind: 'video'; source?: string; storedSource?: string; thumbnail?: string }

export interface DynamicPools {
  /** Images then videos, which is the order they were uploaded in. */
  assets: DynamicAsset[]
  primaryTexts: string[]
  headlines: string[]
  descriptions: string[]
  callToActions: FBCallToAction[]
}

const EMPTY_POOLS: DynamicPools = {
  assets: [],
  primaryTexts: [],
  headlines: [],
  descriptions: [],
  callToActions: [],
}

/** What Facebook has to mix, read off a saved creative. */
export function dynamicPools(creative: AdCreative): DynamicPools {
  if (!isDynamicCreative(creative)) return EMPTY_POOLS
  const config = creative.dynamicConfig
  return {
    assets: [
      ...creative.dynamicImages.map((image): DynamicAsset => ({ kind: 'image', url: image.url })),
      ...creative.dynamicVideos.map(
        (video): DynamicAsset => ({
          kind: 'video',
          source: video.source,
          storedSource: video.storedSource,
          thumbnail: video.thumbnail,
        })
      ),
    ],
    primaryTexts: config.primaryTexts ?? [],
    headlines: config.headlines ?? [],
    descriptions: config.descriptions ?? [],
    callToActions: config.callToActions ?? [],
  }
}

export interface CopyPool {
  key: keyof DynamicCombination
  label: string
  /** Ready to read on screen; the index into this is the index into the pool. */
  values: string[]
}

const spaced = (value: string) => value.replace(/_/g, ' ')

/**
 * Every copy variant the ad can be assembled from, in the order they read down
 * the ad, as text.
 *
 * A pool with a single entry is listed like any other: it is still copy the
 * reviewer has to read, and hiding it would leave them wondering where the
 * description went. Empty pools are dropped — Facebook varies nothing there, so
 * the ad's own field is what ships and it is already on the preview.
 */
export function copyPools(pools: DynamicPools): CopyPool[] {
  return [
    { key: 'primaryText' as const, label: 'Primary text', values: pools.primaryTexts },
    { key: 'headline' as const, label: 'Headline', values: pools.headlines },
    { key: 'description' as const, label: 'Description', values: pools.descriptions },
    {
      key: 'callToAction' as const,
      label: 'Call to action',
      values: pools.callToActions.map(spaced),
    },
  ].filter(pool => pool.values.length > 0)
}

const clampIndex = (index: number, length: number) =>
  Math.min(Math.max(Math.trunc(index) || 0, 0), Math.max(length - 1, 0))

/** Keep a saved selection valid when a focus refetch returns shorter pools. */
export function clampCombination(pools: DynamicPools, at: DynamicCombination): DynamicCombination {
  return {
    asset: clampIndex(at.asset, pools.assets.length),
    primaryText: clampIndex(at.primaryText, pools.primaryTexts.length),
    headline: clampIndex(at.headline, pools.headlines.length),
    description: clampIndex(at.description, pools.descriptions.length),
    callToAction: clampIndex(at.callToAction, pools.callToActions.length),
  }
}

/**
 * One pinned combination, in the shape the ad renderer reads.
 *
 * An empty pool falls back to the creative's own field, which is what the ad
 * ships with when Facebook has nothing to vary there. With no assets at all the
 * format stays `dynamic`, so the renderer shows its honest placeholder rather
 * than an empty image frame.
 */
export function pinnedCombination(
  creative: AdCreative,
  pools: DynamicPools,
  at: DynamicCombination
): LocalAdPreviewCreative {
  const pinned = clampCombination(pools, at)
  const asset = pools.assets[pinned.asset]
  const isVideo = asset?.kind === 'video'

  return {
    format: asset
      ? isVideo
        ? AdCreativeType.single_video
        : AdCreativeType.single_image
      : AdCreativeType.dynamic,
    primaryText: pools.primaryTexts[pinned.primaryText] ?? creative.primaryText,
    imageURL: asset?.kind === 'image' ? asset.url : undefined,
    singleVideo: isVideo
      ? { source: asset.source, storedSource: asset.storedSource, thumbnail: asset.thumbnail }
      : undefined,
    linkHeadline: pools.headlines[pinned.headline] ?? creative.linkHeadline,
    linkDescription: pools.descriptions[pinned.description] ?? creative.linkDescription,
    callToAction: pools.callToActions[pinned.callToAction] ?? creative.callToAction,
    linkDisplayURL: creative.linkDisplayURL,
  }
}
